// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadInvoicePaymentHistory from "@eisinc/App/Handlers/Data/InvoicePayment/LoadInvoicePaymentHistory";
import clearSearch from "@eisinc/App/Handlers/Pages/InvoicePaymentHistory/ClearSearch";
import updateSearchFields from "@eisinc/App/Handlers/Pages/InvoicePaymentHistory/UpdateSearchFields";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import Button, { ButtonProps } from "@eisinc/MobiusOverrides/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@eisinc/MobiusOverrides/DatePicker";
import Tag, { horizontalStyles, TagPresentationProps } from "@eisinc/MobiusOverrides/Tag";
import TextField, { TextFieldProps } from "@eisinc/MobiusOverrides/TextField";
import { INVOICE_PAYMENT_HISTORY_FILTER_KEYS } from "@eisinc/XDL/constants";
import { screen } from "@eisinc/XDL/Helpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface State {
    invoiceNumber: string;
    poNumber: string;
    total: string;
}

const mapStateToProps = (state: CustomAppState) => ({
    isFilterOpen: state.xInvoicePaymentHistoryListState.isFilterOpen,
    getInvoicePaymentHistoryApiParameter: state.xInvoicePaymentHistoryListState.getInvoicePaymentHistoryApiParameter,
});

const mapDispatchToProps = {
    loadCurrentShipTos,
    updateSearchFields,
    loadInvoicePaymentHistory,
    clearSearch,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoicePaymentHistoryFilterStyles {
    container?: GridContainerProps;
    invoiceNumberGridItem?: GridItemProps;
    invoiceNumberText?: TextFieldProps;
    poNumberGridItem?: GridItemProps;
    poNumberText?: TextFieldProps;
    totalGridItem?: GridItemProps;
    totalText?: TextFieldProps;
    fromGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toGridItem?: GridItemProps;
    toDate?: DatePickerPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonProps;
}

export const filterStyles: InvoicePaymentHistoryFilterStyles = {
    container: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    invoiceNumberGridItem: { width: [12, 12, 4, 4, 4] },
    poNumberGridItem: { width: [12, 12, 4, 4, 4] },
    totalGridItem: { width: [12, 12, 4, 4, 4] },
    fromGridItem: {
        width: [6, 6, 3, 3, 2],
        css: css`
            > div {
                width: 100%;
                line-height: 0;
            }
            @media only screen and (max-width: 480px) {
                min-width: 100% !important;
            }
        `,
    },
    toGridItem: {
        width: [6, 6, 3, 3, 2],
        css: css`
            > div {
                width: 100%;
                line-height: 0;
            }
            @media only screen and (max-width: 480px) {
                min-width: 100% !important;
            }
        `,
    },
    appliedFiltersContainer: {
        css: css`
            ${horizontalStyles}
            margin-bottom: 15px;
        `,
    },
    buttonsItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    clearFiltersButton: {
        variant: "secondary",
        css: css`
            ${screen("lt", "sm")`
                width: 100%;
            `}
        `,
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
};

const styles = filterStyles;

class InvoicePaymentHistoryFilter extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            invoiceNumber: this.getValue(props, INVOICE_PAYMENT_HISTORY_FILTER_KEYS.INVOICE_NUMBER),
            poNumber: this.getValue(props, INVOICE_PAYMENT_HISTORY_FILTER_KEYS.PO_REF_NUMBER),
            total: this.getValue(props, INVOICE_PAYMENT_HISTORY_FILTER_KEYS.TOTAL),
        };
    }

    updateTimeoutId: number | undefined;

    getValue = (props: Props, key: string): string => {
        let value = props.getInvoicePaymentHistoryApiParameter?.[key];
        if (!value) {
            value = "";
        }
        return value;
    };

    componentDidUpdate(prevProps: Props) {
        const previousTotalNumber = this.getValue(prevProps, INVOICE_PAYMENT_HISTORY_FILTER_KEYS.TOTAL);
        const currentTotalNumber = this.getValue(this.props, INVOICE_PAYMENT_HISTORY_FILTER_KEYS.TOTAL);
        const previousInvoiceNumber = this.getValue(prevProps, INVOICE_PAYMENT_HISTORY_FILTER_KEYS.INVOICE_NUMBER);
        const currentInvoiceNumber = this.getValue(this.props, INVOICE_PAYMENT_HISTORY_FILTER_KEYS.INVOICE_NUMBER);
        const previousPoNumber = this.getValue(prevProps, INVOICE_PAYMENT_HISTORY_FILTER_KEYS.PO_REF_NUMBER);
        const currentPoNumber = this.getValue(this.props, INVOICE_PAYMENT_HISTORY_FILTER_KEYS.PO_REF_NUMBER);

        if (
            (previousTotalNumber !== currentTotalNumber && currentTotalNumber !== this.state.total) ||
            (previousPoNumber !== currentPoNumber && currentPoNumber !== this.state.poNumber) ||
            (previousInvoiceNumber !== currentInvoiceNumber && currentInvoiceNumber !== this.state.invoiceNumber)
        ) {
            this.setState({
                invoiceNumber: currentInvoiceNumber,
                total: currentTotalNumber,
                poNumber: currentPoNumber,
            });
        }
    }

    updateParameterAfterTimeout = () => {
        if (typeof this.updateTimeoutId === "number") {
            clearTimeout(this.updateTimeoutId);
        }

        this.updateTimeoutId = setTimeout(() => {
            this.props.updateSearchFields({
                [INVOICE_PAYMENT_HISTORY_FILTER_KEYS.INVOICE_NUMBER]: this.state.invoiceNumber,
                [INVOICE_PAYMENT_HISTORY_FILTER_KEYS.TOTAL]: this.state.total,
                [INVOICE_PAYMENT_HISTORY_FILTER_KEYS.PO_REF_NUMBER]: this.state.poNumber,
            });
        }, 250);
    };

    invoiceNumberChangeHandler = (invoiceNumber: string) => {
        this.setState({
            invoiceNumber,
        });

        this.updateParameterAfterTimeout();
    };

    totalChangeHandler = (total: string) => {
        this.setState({
            total,
        });

        this.updateParameterAfterTimeout();
    };

    poNumberChangeHandler = (poNumber: string) => {
        this.setState({
            poNumber,
        });

        this.updateParameterAfterTimeout();
    };

    fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        this.props.updateSearchFields({
            [INVOICE_PAYMENT_HISTORY_FILTER_KEYS.FROM_DATE]: convertDateToDateOnlyString(selectedDay).replace(
                /-/g,
                "/",
            ),
        });
    };

    toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        this.props.updateSearchFields({
            [INVOICE_PAYMENT_HISTORY_FILTER_KEYS.TO_DATE]: convertDateToDateOnlyString(selectedDay).replace(/-/g, "/"),
        });
    };

    showOpenOnlyChangeHandler = (showOpenOnly: boolean) => {
        this.props.updateSearchFields({ showOpenOnly: showOpenOnly || "" } as any);
    };

    clearFiltersClickHandler = () => {
        this.props.clearSearch();
    };

    render() {
        const invoiceNumber =
            this.props.getInvoicePaymentHistoryApiParameter[INVOICE_PAYMENT_HISTORY_FILTER_KEYS.INVOICE_NUMBER];
        const total = this.props.getInvoicePaymentHistoryApiParameter[INVOICE_PAYMENT_HISTORY_FILTER_KEYS.TOTAL];
        const poNumber =
            this.props.getInvoicePaymentHistoryApiParameter[INVOICE_PAYMENT_HISTORY_FILTER_KEYS.PO_REF_NUMBER];
        let fromDate = this.props.getInvoicePaymentHistoryApiParameter[INVOICE_PAYMENT_HISTORY_FILTER_KEYS.FROM_DATE];
        let toDate = this.props.getInvoicePaymentHistoryApiParameter[INVOICE_PAYMENT_HISTORY_FILTER_KEYS.TO_DATE];

        if (!this.props.isFilterOpen) {
            return (
                <StyledWrapper {...styles.appliedFiltersContainer}>
                    {invoiceNumber && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                this.invoiceNumberChangeHandler("");
                            }}
                        >
                            {`${translate("Invoice #")}: ${invoiceNumber}`}
                        </Tag>
                    )}
                    {total && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                this.totalChangeHandler("");
                            }}
                        >
                            {`${translate("Total")}: ${total}`}
                        </Tag>
                    )}
                    {poNumber && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                this.poNumberChangeHandler("");
                            }}
                        >
                            {`${translate("PO/ Reference #")}: ${poNumber}`}
                        </Tag>
                    )}
                    {fromDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                this.fromDateChangeHandler({});
                            }}
                            data-test-selector="invoicePaymentHistory_fromDateButton"
                        >
                            {`${translate("From")}: ${fromDate}`}
                        </Tag>
                    )}
                    {toDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                this.toDateChangeHandler({});
                            }}
                        >
                            {`${translate("To")}: ${toDate}`}
                        </Tag>
                    )}
                </StyledWrapper>
            );
        }

        fromDate = fromDate ? new Date(fromDate.replace(/-/g, "/")) : undefined;
        toDate = toDate ? new Date(toDate.replace(/-/g, "/")) : undefined;

        return (
            <GridContainer {...styles.container}>
                <GridItem {...styles.invoiceNumberGridItem}>
                    <TextField
                        {...styles.invoiceNumberText}
                        value={this.state.invoiceNumber}
                        label={translate("Invoice #")}
                        onChange={event => this.invoiceNumberChangeHandler(event.target.value)}
                        data-test-selector="invoicePaymentHistory_invoiceNumberFilter"
                    />
                </GridItem>
                <GridItem {...styles.poNumberGridItem}>
                    <TextField
                        {...styles.poNumberText}
                        value={this.state.poNumber}
                        label={translate("PO/ Reference #")}
                        onChange={event => this.poNumberChangeHandler(event.target.value)}
                        data-test-selector="invoicePaymentHistory_poNumberFilter"
                    />
                </GridItem>
                <GridItem {...styles.totalGridItem}>
                    <TextField
                        {...styles.totalText}
                        value={this.state.total}
                        label={translate("Total")}
                        onChange={event => this.totalChangeHandler(event.target.value)}
                        data-test-selector="invoicePaymentHistory_totalFilter"
                        type="number"
                    />
                </GridItem>
                <GridItem {...styles.fromGridItem} data-test-selector="invoicePaymentHistory_fromDateFilter">
                    <DatePicker
                        {...styles.fromDate}
                        label={translate("From")}
                        selectedDay={fromDate}
                        onDayChange={this.fromDateChangeHandler}
                        dateTimePickerProps={{
                            clearIcon: null,
                            maxDate: toDate,
                            ...styles.fromDate?.dateTimePickerProps,
                        }}
                    />
                </GridItem>
                <GridItem {...styles.toGridItem} data-test-selector="invoicePaymentHistory_toDateFilter">
                    <DatePicker
                        {...styles.toDate}
                        label={translate("To")}
                        selectedDay={toDate}
                        onDayChange={this.toDateChangeHandler}
                        dateTimePickerProps={{
                            clearIcon: null,
                            minDate: fromDate,
                            ...styles.toDate?.dateTimePickerProps,
                        }}
                    />
                </GridItem>
                <GridItem {...styles.buttonsItem}>
                    <Button
                        {...styles.clearFiltersButton}
                        onClick={this.clearFiltersClickHandler}
                        data-test-selector="invoicePaymentHistory_clearFiltersButton"
                    >
                        {translate("Clear Filters")}
                    </Button>
                </GridItem>
            </GridContainer>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoicePaymentHistoryFilter),
    definition: {
        group: "Invoice Payment History" as any,
        displayName: "Search Results Filter",
        allowedContexts: ["InvoicePaymentHistoryPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
